import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft, Download, Lock } from "lucide-react";
import { useMemo } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useGcCostTracker } from "@/hooks/use-gc-line-items";
import { downloadCsv } from "@/lib/csv";

function formatUSD(value: number | string | null | undefined): string {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

function formatPct(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function GcCostTrackerPage() {
  const { projectId } = useParams({ strict: false }) as { projectId: string };
  const { data, isLoading, isError, error, refetch } = useGcCostTracker(projectId ?? "");

  // Flatten unique periods across all baselines.
  const periodColumns = useMemo(() => {
    const map = new Map<
      string,
      { payAppId: string; applicationNo: number; periodEnd: string | null; status: string }
    >();
    for (const row of data?.rows ?? []) {
      for (const p of row.periods) {
        if (!map.has(p.payAppId)) {
          map.set(p.payAppId, {
            payAppId: p.payAppId,
            applicationNo: p.applicationNo ?? 0,
            periodEnd: p.periodEnd,
            status: p.status,
          });
        }
      }
    }
    return Array.from(map.values()).sort((a, b) => a.applicationNo - b.applicationNo);
  }, [data]);

  const summary = useMemo(() => {
    const rows = data?.rows ?? [];
    return {
      baselines: rows.length,
      budget: rows.reduce((sum, r) => sum + r.totals.budget, 0),
      billed: rows.reduce((sum, r) => sum + r.totals.billedToDate, 0),
      balance: rows.reduce((sum, r) => sum + r.totals.balance, 0),
      locked: rows.filter((r) => r.baseline.budgetLockedAt).length,
    };
  }, [data]);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }
  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load GC cost tracker"}
        onRetry={() => refetch()}
      />
    );
  }

  const rows = data?.rows ?? [];

  const handleExport = () => {
    const csvRows: Record<string, string | number>[] = rows.map((r) => {
      const base: Record<string, string | number> = {
        label: r.baseline.label,
        type: r.baseline.lineType,
        budget: r.totals.budget,
        billedToDate: r.totals.billedToDate,
        balance: r.totals.balance,
        pctComplete: `${(r.totals.pctComplete * 100).toFixed(2)}%`,
      };
      for (const col of periodColumns) {
        const period = r.periods.find((p) => p.payAppId === col.payAppId);
        base[`#${col.applicationNo} this period`] = period?.thisPeriod ?? 0;
        base[`#${col.applicationNo} stored`] = period?.storedMaterials ?? 0;
      }
      return base;
    });
    const columns = [
      { key: "label", label: "Label" },
      { key: "type", label: "Type" },
      { key: "budget", label: "Budget" },
      { key: "billedToDate", label: "Billed to Date" },
      { key: "balance", label: "Balance" },
      { key: "pctComplete", label: "% Complete" },
      ...periodColumns.flatMap((col) => [
        { key: `#${col.applicationNo} this period`, label: `Period #${col.applicationNo} This` },
        { key: `#${col.applicationNo} stored`, label: `Period #${col.applicationNo} Stored` },
      ]),
    ];
    downloadCsv(`gc-cost-tracker-${projectId}.csv`, csvRows, columns);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Link
          to="/projects/$projectId"
          params={{ projectId: projectId ?? "" }}
          search={{ tab: "overview" }}
        >
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">GC Cost Tracker</h1>
          <p className="text-sm text-muted-foreground">
            Per-period draw history for GC-owned cost lines.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleExport} disabled={rows.length === 0}>
          <Download className="mr-1.5 h-4 w-4" />
          Export CSV
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-4 text-sm">
            <Stat label="Baselines" value={String(summary.baselines)} />
            <Stat label="Total budget" value={formatUSD(summary.budget)} />
            <Stat label="Billed to date" value={formatUSD(summary.billed)} />
            <Stat label="Balance" value={formatUSD(summary.balance)} />
            <Stat label="Locked" value={`${summary.locked} / ${summary.baselines}`} />
          </div>
        </CardContent>
      </Card>

      {rows.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No GC line items defined yet. Add them from the project Finance tab.
          </CardContent>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-background">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[200px]">Line</TableHead>
                <TableHead className="text-right">Budget</TableHead>
                <TableHead className="text-right">Billed</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead className="text-right">% Complete</TableHead>
                {periodColumns.map((col) => (
                  <TableHead key={col.payAppId} className="text-right">
                    <div className="flex flex-col items-end text-xs">
                      <span>#{col.applicationNo}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {formatDate(col.periodEnd)}
                      </span>
                      <Badge variant="secondary" className="mt-0.5 text-[9px]">
                        {col.status}
                      </Badge>
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.baseline.id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">{r.baseline.label}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {r.baseline.lineType} · {r.baseline.billingMethod}
                        {r.baseline.budgetLockedAt ? (
                          <Lock className="inline ml-1 h-3 w-3 text-red-600" />
                        ) : null}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatUSD(r.totals.budget)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatUSD(r.totals.billedToDate)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatUSD(r.totals.balance)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatPct(r.totals.pctComplete)}
                  </TableCell>
                  {periodColumns.map((col) => {
                    const p = r.periods.find((pp) => pp.payAppId === col.payAppId);
                    if (!p) {
                      return (
                        <TableCell key={col.payAppId} className="text-right text-muted-foreground">
                          —
                        </TableCell>
                      );
                    }
                    const reason = p.drawReason ?? p.feeOverrideReason ?? null;
                    const cell = (
                      <span className="tabular-nums">
                        {formatUSD(p.thisPeriod + p.storedMaterials)}
                      </span>
                    );
                    return (
                      <TableCell key={col.payAppId} className="text-right">
                        {reason ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="cursor-help underline decoration-dotted">
                                  {cell}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs text-xs">{reason}</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          cell
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-base font-semibold tabular-nums">{value}</div>
    </div>
  );
}
